import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Bell, X, ShoppingCart, CheckCircle, Clock, Package, Truck, XCircle, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_ICONS = {
  new_order: ShoppingCart,
  order_status: Package,
  confirmed: CheckCircle,
  preparing: Clock,
  ready: CheckCircle,
  delivered: Truck,
  cancelled: XCircle,
};

const STATUS_COLORS = {
  new_order: '#D97706',
  confirmed: '#3B82F6',
  preparing: '#F59E0B',
  ready: '#10B981',
  delivered: '#10B981',
  cancelled: '#EF4444',
};

export default function NotificationCenter() {
  const { API, authHeaders, activeBranch, token } = useAuth();
  const { lang } = useLanguage();
  const { t } = useTranslation();
  const isAr = lang === 'ar';
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('zouqai_notif_sound') !== 'off');
  const wsRef = useRef(null);
  const audioRef = useRef(null);
  const panelRef = useRef(null);

  // Load notifications on mount
  const fetchNotifications = useCallback(async () => {
    if (!token || !activeBranch?.id) return;
    try {
      const { data } = await axios.get(`${API}/notifications?branch_id=${activeBranch.id}`, authHeaders());
      setNotifications(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* silent */ }
  }, [API, authHeaders, activeBranch, token]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // WebSocket connection
  useEffect(() => {
    if (!activeBranch?.id || !token) return;
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/dashboard/${activeBranch.id}`);
    wsRef.current = ws;

    ws.onopen = () => {
      // Keep alive ping
      const ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
      }, 30000);
      ws._pingInterval = ping;
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.event === 'notification' && msg.data) {
          const notif = msg.data;
          setNotifications(prev => [notif, ...prev].slice(0, 30));
          setUnread(prev => prev + 1);

          // Play sound
          if (soundEnabled && audioRef.current) {
            audioRef.current.play().catch(() => {});
          }

          // Show toast
          const title = isAr ? notif.title_ar : notif.title_en;
          const body = isAr ? notif.body_ar : notif.body_en;
          const isNewOrder = notif.type === 'new_order';
          toast(title, {
            description: body,
            icon: isNewOrder ? '🛒' : '📦',
            duration: 5000,
          });
        }
      } catch { /* ignore */ }
    };

    ws.onclose = () => {
      if (ws._pingInterval) clearInterval(ws._pingInterval);
    };

    return () => {
      if (ws._pingInterval) clearInterval(ws._pingInterval);
      ws.close();
    };
  }, [activeBranch?.id, token, API, soundEnabled, isAr]);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    if (!activeBranch?.id) return;
    try {
      await axios.put(`${API}/notifications/read-all?branch_id=${activeBranch.id}`, {}, authHeaders());
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* silent */ }
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('zouqai_notif_sound', next ? 'on' : 'off');
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return isAr ? 'الآن' : 'now';
    if (mins < 60) return isAr ? `${mins} د` : `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return isAr ? `${hrs} س` : `${hrs}h`;
    return isAr ? `${Math.floor(hrs / 24)} ي` : `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Notification sound */}
      <audio ref={audioRef} preload="auto" src="data:audio/wav;base64,UklGRl4FAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YToFAACAgICAgICAgICAgICAgICAgICBhYqQlpudnp2bmJSPioWBfnx7e3x9f4KFiY2RlZibnJ2dnJqXlJCLhoJ+fHt7fH1/goWJjZGVmJucnZ2cmpeTj4qGgn58e3t8fYCDho2RlZmbm52dm5mVkY2Jg398e3t8fYCDho2RlZmbm52dm5mVkY2Jg398e3t8fYCDho2RlZqbm52dm5mVkY2Ig398e3t8fn+DhoyQlJibnJydm5qWko6Kg4B8e3t8fn+DhoyQlJibnJydm5qWko6Kg4B9e3t7fX+ChYmNkZWYm5ydnZyalpKOioWBfnt7e3x9f4KFiY2RlZibnJ2dnJqWk4+KhYF+fHt7fH1/goWJjZGVmJucnZ2cmpeTj4qGgX58e3t8fX+ChYmNkZWYm5ydnZyalpOPioaBfnx7e3x9f4KFiY2RlZibnJ2dnJqWk4+KhYF+fHt7fH1/goWJjZGVmJucnZ2cmpaTkIuGgn58e3t8fX+ChYmNkZWYm5ydnZyalpOPioaBfnx7e31/goaKjpKWmZucnJ2cm5mVkY2JhIB9e3t7fH5/goaKjpKWmZucnJ2cm5mVkY2JhIB9e3t7fH5/goaKjpKWmZucnJ2cm5mVkY2JhIB9fHt7fH5/goaKjpKWmZucnJ2cm5mVkY2JhIB9fHt7fH5/goaKjpKWmZucnJ2cm5mVkY2JhIB9fHt7fH5/goaKjpKWmZucnJ2cm5mVkY2JhIB9fHt7fH5/goaKjpKWmZucnJ2cm5mVkY2JhIB9fHt7fH5/g4eL" />

      {/* Bell Button */}
      <button onClick={() => setOpen(!open)} data-testid="notification-bell"
        className="relative h-8 w-8 rounded-lg flex items-center justify-center transition-colors hover:bg-accent">
        <Bell className="w-4 h-4 text-muted-foreground" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="notification-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div data-testid="notification-panel"
          className="absolute top-full mt-2 end-0 w-80 md:w-96 max-h-[480px] rounded-xl border border-border bg-card shadow-xl overflow-hidden z-50"
          style={{ animation: 'fadeIn 0.2s ease-out' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {isAr ? 'الإشعارات' : 'Notifications'}
              {unread > 0 && <span className="ms-1.5 text-xs font-normal text-muted-foreground">({unread})</span>}
            </h3>
            <div className="flex items-center gap-1.5">
              <button onClick={toggleSound} data-testid="notification-sound-toggle"
                className="p-1.5 rounded-md hover:bg-accent transition-colors" title={soundEnabled ? 'Mute' : 'Unmute'}>
                {soundEnabled ? <Volume2 className="w-3.5 h-3.5 text-muted-foreground" /> : <VolumeX className="w-3.5 h-3.5 text-muted-foreground" />}
              </button>
              {unread > 0 && (
                <button onClick={markAllRead} data-testid="notification-read-all"
                  className="text-[11px] text-primary hover:underline font-medium px-2 py-1 rounded-md hover:bg-accent transition-colors">
                  {isAr ? 'قراءة الكل' : 'Read all'}
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 rounded-md hover:bg-accent transition-colors">
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto max-h-[400px]">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">{isAr ? 'لا توجد إشعارات' : 'No notifications'}</p>
              </div>
            ) : (
              notifications.map((notif, i) => {
                const Icon = STATUS_ICONS[notif.status] || STATUS_ICONS[notif.type] || Bell;
                const color = STATUS_COLORS[notif.status] || STATUS_COLORS[notif.type] || '#6B7280';
                const title = isAr ? notif.title_ar : notif.title_en;
                const body = isAr ? notif.body_ar : notif.body_en;
                return (
                  <div key={notif.id || i} data-testid={`notification-item-${i}`}
                    className="flex items-start gap-3 px-4 py-3 border-b border-border/50 transition-colors hover:bg-accent/50"
                    style={{ background: notif.read ? 'transparent' : 'hsl(var(--primary) / 0.03)' }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${color}15` }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{title}</p>
                        <span className="text-[10px] text-muted-foreground flex-shrink-0">{timeAgo(notif.created_at)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{body}</p>
                    </div>
                    {!notif.read && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
